/**
 * Mg Theme — Tabs Block
 * © 2026 NAMY SOLUTIONS LLC – All Rights Reserved.
 */

if (!customElements.get('mg-tabs')) {
  class MgTabs extends HTMLElement {
    connectedCallback() {
      /* Un composant relogé dans le DOM re-entre par ici. Sans garde, chaque
         passage empile un second jeu d'ecouteurs sur les memes onglets. */
      if (this._initialized) return;
      this._initialized = true;

      this.buildTablist();
      this.watchOverflow();
      this.watchOrientation();

      this.tabs = this._mesOnglets();
      this.panels = this._mesPanneaux();

      if (this.tabs.length === 0) return;

      this._tabClickHandlers = [];
      this._tabKeydownHandlers = [];

      this.tabs.forEach((tab) => {
        var clickHandler = () => this.selectTab(tab);
        var keydownHandler = (e) => this.handleKeydown(e);
        tab.addEventListener('click', clickHandler);
        tab.addEventListener('keydown', keydownHandler);
        this._tabClickHandlers.push({ tab: tab, handler: clickHandler });
        this._tabKeydownHandlers.push({ tab: tab, handler: keydownHandler });
      });
    }

    /* Un carrousel imbrique porte LES MEMES roles : Splide met `role="tabpanel"`
       sur chaque diapositive et `role="tab"` sur chaque puce de pagination. Une
       recherche dans tout le sous-arbre les adopte — et le controleur pose alors
       `hidden` sur les diapositives d'un carrousel, qui ne revient jamais.
       Un role a nous ne vit jamais dans un `.splide`. */
    _aMoi(el) {
      return !el.closest('.splide');
    }

    _mesOnglets() {
      var self = this;
      return Array.from(this.querySelectorAll('[role="tab"]')).filter(function (t) {
        return self._aMoi(t);
      });
    }

    /* Le panneau se DEDUIT de l'onglet qui le commande, plutot que d'etre
       cherche par son role : la relation `aria-controls` est portee par notre
       propre markup, donc elle ne peut designer que nos panneaux. */
    _mesPanneaux() {
      var self = this;
      var vus = [];
      this.tabs.forEach(function (tab) {
        var id = tab.getAttribute('aria-controls');
        if (!id) return;
        var panel = self.querySelector('#' + CSS.escape(id));
        if (panel && vus.indexOf(panel) === -1) vus.push(panel);
      });
      return vus;
    }

    /* Each _mg-tab renders its own button and panel, because a parent block
       cannot read a theme block child's settings. The markup therefore ships
       WITHOUT tab roles — a role="tab" outside a tablist is invalid ARIA, and
       without JS there is no tablist. Here we build it, move the buttons in,
       and apply the roles: valid ARIA when scripted, a readable
       title-then-content stack when not. */
    buildTablist() {
      var buttons = Array.from(this.querySelectorAll('[data-tab-button]'));
      if (!buttons.length) return;
      if (this.querySelector('[role="tablist"]')) return;   // idempotent on editor re-render

      var panelsHost = this.querySelector('.mg-tabs__panels') || this;
      var list = document.createElement('div');
      list.className = 'mg-tabs__list';
      list.setAttribute('role', 'tablist');
      var label = panelsHost.getAttribute('data-tablist-label');
      if (label) list.setAttribute('aria-label', label);
      panelsHost.parentNode.insertBefore(list, panelsHost);

      buttons.forEach(function (button, index) {
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.setAttribute('tabindex', index === 0 ? '0' : '-1');
        list.appendChild(button);

        var panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        panel.setAttribute('role', 'tabpanel');
        /* Focusable so keyboard users can reach panel content that holds no
           control of its own (WAI-ARIA tabs pattern). */
        panel.setAttribute('tabindex', '0');
        panel.hidden = index !== 0;
      });
    }

    /* Un panneau differe garde son contenu dans un <template> : analyse, mais
       hors de l'arbre rendu — aucune mise en page, aucune image chargee, aucun
       composant monte. On le greffe a la PREMIERE ouverture, une seule fois.
       Sans <template>, la methode ne fait rien : les autres hotes du
       controleur ne changent pas de comportement. */
    hydratePanel(panel) {
      var tpl = panel.querySelector(':scope > template[data-ct-deferred]');
      if (!tpl) return;
      panel.insertBefore(tpl.content.cloneNode(true), tpl);
      tpl.remove();
    }

    /* UNE seule definition de « la liste est-elle empilee ». Le clavier et
       l'attribut ARIA la partagent : deux expressions jumelles finiraient par
       diverger, et l'utilisateur se verrait promettre des touches inexistantes.
       Le seuil colle au `@media (width > 750px)` qui empile reellement. */
    isVertical() {
      return this.dataset.disposition === 'sidebar'
        && window.matchMedia
        && window.matchMedia('(width > 750px)').matches;
    }

    /* `aria-orientation` ne se regle pas : il se DEDUIT. Le defaut d'un tablist
       est horizontal, donc une liste empilee annoncerait l'inverse de ce que
       son propre clavier attend. */
    syncOrientation() {
      var list = this.querySelector('[role="tablist"]');
      if (!list) return;
      if (this.isVertical()) list.setAttribute('aria-orientation', 'vertical');
      else list.removeAttribute('aria-orientation');
    }

    watchOrientation() {
      if (!window.matchMedia) return;
      this._orientationQuery = window.matchMedia('(width > 750px)');
      this._onOrientation = this.syncOrientation.bind(this);
      this.syncOrientation();
      if (this._orientationQuery.addEventListener) {
        this._orientationQuery.addEventListener('change', this._onOrientation);
      }
    }

    /* Opt-in : sans l'attribut, rien ne s'installe sur les autres hotes de <mg-tabs>. */
    watchOverflow() {
      var bar = this.querySelector('[data-overflow-watch]');
      if (!bar || !window.Mg || !Mg.watchOverflow) return;
      this._unwatchOverflow = Mg.watchOverflow(bar, this.isVertical.bind(this));
    }

    disconnectedCallback() {
      if (this._tabClickHandlers) {
        this._tabClickHandlers.forEach(function(entry) {
          entry.tab.removeEventListener('click', entry.handler);
        });
      }
      if (this._tabKeydownHandlers) {
        this._tabKeydownHandlers.forEach(function(entry) {
          entry.tab.removeEventListener('keydown', entry.handler);
        });
      }
      if (this._unwatchOverflow) {
        this._unwatchOverflow();
        this._unwatchOverflow = null;
      }
      if (this._orientationQuery && this._onOrientation
          && this._orientationQuery.removeEventListener) {
        this._orientationQuery.removeEventListener('change', this._onOrientation);
      }
      this._orientationQuery = null;
      this._onOrientation = null;
      /* Les tableaux gardent des references aux noeuds : les vider evite qu'un
         remontage empile des entrees deja retirees. */
      this._tabClickHandlers = null;
      this._tabKeydownHandlers = null;
      this._initialized = false;
    }

    selectTab(tab, opts) {
      this.tabs.forEach((t) => {
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      });

      this.panels.forEach((p) => {
        p.hidden = true;
      });

      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('tabindex', '0');
      /* L'editeur selectionne un bloc sans que personne n'ait demande le
         focus : le prendre deplacerait la vue sous le curseur du marchand. */
      if (!opts || opts.focus !== false) tab.focus();

      const panelId = tab.getAttribute('aria-controls');
      const panel = this.querySelector('#' + CSS.escape(panelId));
      if (panel) {
        this.hydratePanel(panel);
        panel.hidden = false;
        /* Le fondu ne joue qu'a la REVELATION, jamais au chargement. Le helper
           du theme rejoue une animation deja posee (retrait, reflow, ajout). */
        if (window.Mg && Mg.replayAnimation) {
          Mg.replayAnimation(panel, 'is-ct-entering');
        } else {
          panel.classList.add('is-ct-entering');
        }
        /* Le panneau est visible : ceux qui ont refuse de se monter dans le noir
           peuvent le faire maintenant, avec une largeur reelle. Publie APRES
           `hidden = false`, sinon l'abonne mesure encore zero. */
        if (window.MgEvents) {
          MgEvents.publish(MgEvents.Events.PANEL_REVEALED, { panel: panel });
        }
      }
    }

    handleKeydown(e) {
      const index = this.tabs.indexOf(e.currentTarget);
      let next = -1;

      /* Les fleches verticales ne valent QUE pour une liste reellement empilee.
         Sur une rangee horizontale, Bas et Haut appartiennent au defilement de
         la page : les capturer volerait a l'utilisateur son geste principal.
         Evalue a la frappe, donc jamais perime par un redimensionnement. */
      var vertical = this.isVertical();
      /* En ecriture de droite a gauche, l'onglet SUIVANT est a gauche : une
         fleche doit suivre le sens de lecture, pas la geometrie de l'ecran.
         L'axe vertical, lui, ne s'inverse pas. */
      var rtl = getComputedStyle(this).direction === 'rtl';
      var avance = rtl ? 'ArrowLeft' : 'ArrowRight';
      var recule = rtl ? 'ArrowRight' : 'ArrowLeft';

      if (e.key === avance || (vertical && e.key === 'ArrowDown')) {
        next = (index + 1) % this.tabs.length;
      } else if (e.key === recule || (vertical && e.key === 'ArrowUp')) {
        next = (index - 1 + this.tabs.length) % this.tabs.length;
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = this.tabs.length - 1;
      }

      if (next >= 0) {
        e.preventDefault();
        this.selectTab(this.tabs[next]);
      }
    }
  }

  /* UNE seule ecoute pour tous les <mg-tabs> de la page — un ecouteur par
     instance fuirait a chaque re-rendu de l'editeur. Sans elle, le marchand qui
     selectionne un onglet dans la barre laterale regle un panneau qu'il ne voit
     pas. Benefice partage par les trois composants qui montent ce controleur. */
  if (!window.__namyTabsEditorBound) {
    window.__namyTabsEditorBound = true;
    document.addEventListener('shopify:block:select', function (e) {
      var el = e.target;
      if (!el || !el.closest) return;
      var host = el.closest('mg-tabs');
      if (!host || typeof host.selectTab !== 'function') return;

      var tab = el.matches('[role="tab"]') ? el : null;
      if (!tab && el.id) {
        tab = host.querySelector('[aria-controls="' + CSS.escape(el.id) + '"]');
      }
      if (!tab) {
        var labelledBy = el.getAttribute && el.getAttribute('aria-labelledby');
        if (labelledBy) tab = host.querySelector('#' + CSS.escape(labelledBy));
      }
      if (tab) host.selectTab(tab, { focus: false });
    });
  }

  customElements.define('mg-tabs', MgTabs);
}
